/**
 * Dispatched by {@link @databases/table-resizer} during column drag-resize.
 *
 * ResizeObserver alone cannot detect column resizes because the observed
 * scroll container's own dimensions do not change -- only the internal
 * cell widths do. This event bridges the gap.
 *
 * @see {@link @databases/table-resizer!TableResizer} (producer)
 */
const COLUMN_RESIZE_EVENT = 'database-column-resize';

export const RESIZE_OBSERVER_ELEMENT_ID = 'resizeObserver';

type ResizeCallback = () => void;

const subscribers = new Map<Element, Set<ResizeCallback>>();

let observer: ResizeObserver | null = null;
let columnResizeRafId: number | null = null;

/**
 * Handles ResizeObserver notifications with targeted dispatch -- only
 * subscribers of the elements that actually changed are notified.
 *
 * Called synchronously by the browser after layout, so DOM measurements
 * inside callbacks will read fresh geometry.
 */
function handleObserverEntries(entries: ResizeObserverEntry[]) {
  for (const entry of entries) {
    subscribers.get(entry.target)?.forEach((cb) => cb());
  }
}

/**
 * Handles column-resize custom events by notifying all subscribers,
 * since a column width change affects highlights across the entire table.
 *
 * Deferred to rAF because the event is dispatched synchronously from
 * React's handleDrag, before the state update has been committed to the
 * DOM. The rAF ensures measurements run against the freshly-painted
 * column widths.
 */
function scheduleNotifyAll() {
  if (columnResizeRafId !== null) {
    cancelAnimationFrame(columnResizeRafId);
  }

  columnResizeRafId = requestAnimationFrame(() => {
    columnResizeRafId = null;

    subscribers.forEach((callbacks) => {
      callbacks.forEach((cb) => cb());
    });
  });
}

/**
 * Subscribes to layout changes for a given element using a shared singleton
 * ResizeObserver. Also listens for `database-column-resize` custom events
 * dispatched during column drag-resize.
 *
 * Two notification paths, each tuned for correct measurement timing:
 * - **ResizeObserver** -- fires after browser layout; callbacks run synchronously.
 * - **Column resize event** -- fires before React commits; callbacks are
 *   deferred to rAF so the DOM reflects the new column widths.
 *
 * @returns An unsubscribe function. Safe to call multiple times.
 */
export function createResizeListener(
  element: Element,
  onResize: ResizeCallback
): () => void {
  if (!observer) {
    observer = new ResizeObserver(handleObserverEntries);
    document.addEventListener(COLUMN_RESIZE_EVENT, scheduleNotifyAll);
  }

  let elementSubscribers = subscribers.get(element);

  if (!elementSubscribers) {
    elementSubscribers = new Set();

    subscribers.set(element, elementSubscribers);
    observer.observe(element);
  }

  elementSubscribers.add(onResize);

  return function unsubscribe() {
    elementSubscribers?.delete(onResize);

    if (elementSubscribers?.size === 0) {
      subscribers.delete(element);
      observer?.unobserve(element);
    }

    if (subscribers.size === 0) {
      observer?.disconnect();
      observer = null;
      document.removeEventListener(COLUMN_RESIZE_EVENT, scheduleNotifyAll);

      if (columnResizeRafId !== null) {
        cancelAnimationFrame(columnResizeRafId);

        columnResizeRafId = null;
      }
    }
  };
}
